import type { Blok, Subtema } from '../../types';

/**
 * Subtema 1 - Binatang Peliharaan. One week.
 *
 * Days 1-3 follow "Skenario IFP, 16 Layar, versi 3.0" for RPPH 1 (kucing),
 * RPPH 2 (anjing) and RPPH 3 (kelinci): sixteen screens in the same seven
 * blocks each day, three teacher paths, one yard that fills over the week.
 * Days 4 and 5 are still the RPPM's outline and are shown as planned.
 *
 * Content is Bahasa Indonesia and the surrounding code is English: the words a
 * teacher or child sees are the real thing, the words a developer sees are not
 * translated.
 *
 * Three days, three values, on purpose - the spec's correction of an RPPH that
 * wrote the same NAM sentence three times: Hari 1 kasih sayang, Hari 2
 * amanah, Hari 3 kelembutan terhadap yang kecil.
 */

const BLOK: Record<Blok, string> = {
  A: 'Pembuka',
  B: 'Mengamati & Bahasa',
  C: 'Suara & Perasaan',
  D: 'Gerak & Tenang',
  E: 'Memilah & Merawat',
  F: 'Seni & Jati Diri',
  G: 'Penutup',
};

export const binatangPeliharaan: Subtema = {
  id: 'binatang-peliharaan',
  tema: 'Binatang',
  subtema: 'Binatang Peliharaan',
  jenjang: 'TK A (4-5 tahun)',
  minggu: 1,

  modules: [
    {
      id: 'rpph-1-kucing',
      hari: 1,
      hewan: 'kucing',
      title: 'Mengenal Kucing',
      summary: 'Suara, bagian tubuh, berhitung, huruf K, perasaan kucing, memberi makan, dan kucing pertama di Halaman Kita.',
      nilai: 'kasih sayang',
      durasiMenit: 120,
      status: 'ready',
      jalur: { inti: [1, 4, 5, 7, 9, 11, 14, 16], singkat: [1, 4, 11, 14, 16] },
      istirahatSetelah: 7,
      blokNama: BLOK,
      tujuan: [
        { dimensi: 'NAM', tujuan: 'Anak menunjukkan sikap menyayangi kucing sebagai ciptaan Tuhan' },
        { dimensi: 'Fisik Motorik', tujuan: 'Anak mampu menirukan gerakan kucing (merangkak, meregangkan badan)' },
        { dimensi: 'Bahasa', tujuan: 'Anak menyebutkan nama, ciri, dan suara kucing ("meong")' },
        { dimensi: 'Kognitif', tujuan: 'Anak mengenal bagian tubuh kucing dan makanannya' },
        { dimensi: 'Seni', tujuan: 'Anak mewarnai/menempel bentuk kucing sederhana' },
        { dimensi: 'Sosial Emosional', tujuan: 'Anak berani bercerita tentang kucing yang ia warnai dan namai' },
      ],
      load: () => import('./rpph-1-kucing'),
    },

    {
      id: 'rpph-2-anjing',
      hari: 2,
      hewan: 'anjing',
      title: 'Mengenal Anjing',
      summary: 'Suara dan tugas anjing, bagian tubuh, huruf K dan A, merawat setiap hari, dan sikap aman saat bertemu anjing asing.',
      nilai: 'amanah',
      teaser: 'binatang yang setia menjaga rumah',
      durasiMenit: 120,
      status: 'ready',
      jalur: { inti: [1, 4, 5, 9, 11, 13, 14, 16], singkat: [1, 4, 11, 13, 16] },
      istirahatSetelah: 5,
      blokNama: { ...BLOK, E: 'Merawat & Aman' },
      tujuan: [
        { dimensi: 'NAM', tujuan: 'Anak menunjukkan sikap menyayangi anjing sebagai ciptaan Tuhan' },
        { dimensi: 'Fisik Motorik', tujuan: 'Anak mampu menirukan gerakan anjing (berlari, duduk, menggerakkan ekor)' },
        { dimensi: 'Bahasa', tujuan: 'Anak menyebutkan nama, ciri, dan suara anjing ("guk guk")' },
        {
          dimensi: 'Kognitif',
          tujuan: 'Anak mengenal bagian tubuh anjing dan makanannya, serta perannya (menjaga rumah, setia)',
        },
        { dimensi: 'Seni', tujuan: 'Anak mewarnai/menempel bentuk anjing sederhana' },
        {
          dimensi: 'Sosial Emosional',
          tujuan: 'Anak berani bercerita pengalaman dengan anjing dan memahami sikap hati-hati saat bertemu anjing asing',
        },
      ],
      load: () => import('./rpph-2-anjing'),
    },

    {
      id: 'rpph-3-kelinci',
      hari: 3,
      hewan: 'kelinci',
      title: 'Mengenal Kelinci',
      summary: 'Menebak dari gerakan, bagian tubuh, panjang atau pendek, memegang dengan lembut, dan Tiga Sahabat yang menutup subtema.',
      nilai: 'kelembutan terhadap yang kecil',
      teaser: 'binatang yang telinganya panjang',
      durasiMenit: 120,
      status: 'ready',
      jalur: { inti: [1, 4, 9, 11, 12, 13, 14, 16], singkat: [1, 4, 12, 14, 16] },
      istirahatSetelah: 11,
      blokNama: { ...BLOK, C: 'Ciri & Perasaan', E: 'Merawat & Lembut' },
      tujuan: [
        { dimensi: 'NAM', tujuan: 'Anak menunjukkan sikap menyayangi kelinci sebagai ciptaan Tuhan' },
        {
          dimensi: 'Fisik Motorik',
          tujuan: 'Anak mampu menirukan gerakan kelinci (melompat, menggerakkan hidung, telinga tegak)',
        },
        { dimensi: 'Bahasa', tujuan: 'Anak menyebutkan nama dan ciri kelinci (telinga panjang, ekor pendek, bulu lembut)' },
        { dimensi: 'Kognitif', tujuan: 'Anak mengenal bagian tubuh kelinci dan makanan kesukaannya (rumput, sayuran daun)' },
        { dimensi: 'Seni', tujuan: 'Anak mewarnai/menempel bentuk kelinci sederhana' },
        {
          dimensi: 'Sosial Emosional',
          tujuan: 'Anak berani bercerita pengalaman dengan kelinci dan belajar bersikap lembut saat memegang binatang kecil',
        },
      ],
      load: () => import('./rpph-3-kelinci'),
    },

    {
      id: 'rpph-4-ikan-burung',
      hari: 4,
      title: 'Mengenal Ikan & Burung',
      summary: 'Klasifikasi habitat air dan udara, tirukan gerak berenang dan terbang, seret makanan, mewarnai.',
      durasiMenit: 120,
      status: 'planned',
      tujuan: [],
    },
    {
      id: 'rpph-5-puncak-tema',
      hari: 5,
      title: 'Puncak Tema: Aku Sayang Binatang Peliharaanku',
      summary: 'Kuis tebak binatang, estafet gerak, bermain peran merawat binatang, bercerita, dan kolase poster kelas.',
      durasiMenit: 120,
      status: 'planned',
      tujuan: [],
    },
  ],
};

export const subtemas: Subtema[] = [binatangPeliharaan];

export function findSubtema(id: string): Subtema | undefined {
  return subtemas.find((s) => s.id === id);
}

export function findModule(moduleId: string) {
  for (const subtema of subtemas) {
    const module = subtema.modules.find((m) => m.id === moduleId);
    if (module) return { subtema, module };
  }
  return undefined;
}
